/**
 * Google Analytics 4 – only with consent (GDPR / TKG: the _ga cookies are not necessary for the game). The tag is
 * loaded after "Zustimmen" and only on the live domain; "Ablehnen" or a revoke stores the choice and removes the
 * _ga cookies. The choice lives in localStorage `bau-run.consent` (listed in the privacy policy on ructec.com).
 */
export const GA_MEASUREMENT_ID = 'G-1S6YGWQ2M9';
export const CONSENT_KEY = 'bau-run.consent';
export const LIVE_HOSTS: readonly string[] = ['bauralley.com', 'www.bauralley.com'];
export const PRIVACY_URL = 'https://ructec.com/datenschutz#bauralley';
/** Fired (on window) by the "Cookies" link of the menu footer: show the choice again. */
export const CONSENT_EVENT = 'bau-run:consent';

export type Consent = 'granted' | 'denied' | null;

export interface ConsentStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export function readConsent(storage: ConsentStorage | null): Consent {
  try {
    const value = storage?.getItem(CONSENT_KEY);
    return value === 'granted' || value === 'denied' ? value : null;
  } catch {
    return null;
  }
}

export function writeConsent(storage: ConsentStorage | null, consent: 'granted' | 'denied'): void {
  try {
    storage?.setItem(CONSENT_KEY, consent);
  } catch {
    /* private mode: the choice holds for this visit only */
  }
}

function safeStorage(): ConsentStorage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

let loaded = false;

function loadGoogleAnalytics(): void {
  if (loaded || !LIVE_HOSTS.includes(window.location.hostname)) return;
  loaded = true;
  const w = window as unknown as { dataLayer: unknown[]; gtag: (...args: unknown[]) => void };
  w.dataLayer = w.dataLayer || [];
  // gtag pushes the arguments object itself – the GA snippet's own form
  w.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    w.dataLayer.push(arguments);
  };
  w.gtag('js', new Date());
  w.gtag('config', GA_MEASUREMENT_ID);
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
}

/** Removes the GA cookies (_ga, _ga_<id>) on this host and its parent domain. */
function removeGaCookies(): void {
  const host = window.location.hostname;
  const domains = ['', host, `.${host.replace(/^www\./, '')}`];
  for (const part of document.cookie.split(';')) {
    const name = part.split('=')[0]?.trim() ?? '';
    if (!name.startsWith('_ga')) continue;
    for (const domain of domains) document.cookie = `${name}=; Max-Age=0; path=/${domain ? `; domain=${domain}` : ''}`;
  }
}

/**
 * Consent dialog (user 2026-10-04: "dass man diese wirklich bestätigen muss, um zu spielen" – and so that one clicks
 * "Zustimmen" faster): on the first visit it covers the start screen and nothing reaches the game until a choice is
 * made. "Zustimmen" is the big focused main button (Enter confirms); declining stays a visible second button and the
 * game is played just the same – GDPR: the consent has to be free, a game behind a forced "yes" would not be. Shown
 * again from the footer link.
 */
export function initAnalytics(parent: HTMLElement = document.body): void {
  const storage = safeStorage();
  const overlay = document.createElement('div');
  overlay.className = 'consent';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-labelledby', 'consent-title');
  const card = document.createElement('div');
  card.className = 'consent-card';
  const icon = document.createElement('div');
  icon.className = 'consent-icon';
  icon.textContent = '🍪';
  const title = document.createElement('h2');
  title.id = 'consent-title';
  title.textContent = 'Bevor du losrennst';
  const text = document.createElement('p');
  text.append('Wir möchten mit Google Analytics messen, wie BAU RALLEY gespielt wird – damit wir es besser machen können. Dafür setzt Google Cookies (_ga). Ohne Zustimmung wird nichts gemessen. ');
  const link = document.createElement('a');
  link.href = PRIVACY_URL;
  link.target = '_blank';
  link.rel = 'noopener';
  link.textContent = 'Datenschutz';
  text.append(link);
  const actions = document.createElement('div');
  actions.className = 'consent-actions';
  const close = (choice: 'granted' | 'denied'): void => {
    const before = readConsent(storage);
    writeConsent(storage, choice);
    overlay.classList.remove('is-visible');
    if (choice === 'granted') loadGoogleAnalytics();
    else {
      removeGaCookies();
      if (before === 'granted' && loaded) window.location.reload(); // the loaded tag cannot be unloaded
    }
  };
  const button = (label: string, className: string, choice: 'granted' | 'denied'): HTMLButtonElement => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = className;
    b.textContent = label;
    b.addEventListener('click', (event) => {
      event.stopPropagation();
      close(choice);
    });
    actions.append(b);
    return b;
  };
  const accept = button('Zustimmen & losspielen', 'consent-accept', 'granted');
  button('Nur notwendige', 'consent-decline', 'denied');
  card.append(icon, title, text, actions);
  overlay.append(card);
  // taps on the dialog never reach the game as a gesture
  overlay.addEventListener('pointerdown', (event) => event.stopPropagation());
  parent.append(overlay);
  // while it is open no key reaches the game (Enter / Space only press the focused button)
  window.addEventListener(
    'keydown',
    (event) => {
      if (!overlay.classList.contains('is-visible')) return;
      if (event.target instanceof Node && overlay.contains(event.target) && ['Enter', ' ', 'Tab'].includes(event.key)) return;
      event.preventDefault();
      event.stopPropagation();
      if (event.key === 'Enter') accept.click();
    },
    { capture: true },
  );
  const show = (): void => {
    overlay.classList.add('is-visible');
    accept.focus({ preventScroll: true });
  };

  const consent = readConsent(storage);
  if (consent === 'granted') loadGoogleAnalytics();
  if (consent === null) show();
  window.addEventListener(CONSENT_EVENT, show);
}